import { Link } from '@sortlist-frontend/design-system';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { formatCurrency } from '@sortlist-frontend/utils';

import { rankingConfig } from '../../ranking.config';
import type { RankingFullRankingEntry } from '../../types';
import { agencyProfilePath } from '../../utils/agencyProfilePath';
import { RatingStat } from '../RatingStat';
import { ScrollableTable } from '../ScrollableTable';

interface FullRankingTableProps {
  caption: string;
  entries: readonly RankingFullRankingEntry[];
  locale: string;
  isFilterable?: boolean;
}

const filterAttributes = (entry: RankingFullRankingEntry) => ({
  'data-name': entry.name.toLowerCase(),
  'data-country': entry.country,
  'data-rank': entry.rank,
  'data-rating': entry.rating,
  'data-reviews': entry.reviewCount,
  'data-budget': entry.budgetFrom ?? '',
});

const HEADER_CELL_CLASS = 'p-3 text-xs font-semibold uppercase tracking-wider text-secondary-500';

export const FullRankingTable = (props: FullRankingTableProps) => {
  const { caption, entries, locale, isFilterable = false } = props;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);

  return (
    <ScrollableTable>
      <table className="w-full min-w-[640px] text-left text-sm text-secondary-700">
        <caption className="sr-only">{caption}</caption>
        <thead className="bg-secondary-50">
          <tr>
            <th scope="col" className={HEADER_CELL_CLASS}>
              {t('longtail:ranking.table.rank')}
            </th>
            <th scope="col" className={HEADER_CELL_CLASS}>
              {t('longtail:ranking.table.agency')}
            </th>
            <th scope="col" className={HEADER_CELL_CLASS}>
              {t('longtail:ranking.table.basedIn')}
            </th>
            <th scope="col" className={HEADER_CELL_CLASS}>
              {t('longtail:ranking.table.rating')}
            </th>
            {isFilterable ? (
              <th scope="col" className={HEADER_CELL_CLASS}>
                {t('longtail:ranking.table.from')}
              </th>
            ) : null}
            <th scope="col" className={HEADER_CELL_CLASS}>
              {t('longtail:ranking.table.publishedWork')}
            </th>
            <th scope="col" className={HEADER_CELL_CLASS}>
              {t('longtail:ranking.table.score')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-200">
          {entries.map((entry) => (
            <tr
              key={entry.slug}
              className="transition-colors hover:bg-secondary-50"
              {...(isFilterable ? filterAttributes(entry) : {})}>
              <td className="p-3 font-semibold text-primary-600">{entry.rank}</td>
              <td className="p-3">
                <Link
                  href={agencyProfilePath(entry.slug)}
                  color="foreground"
                  underline="always"
                  className="text-sm font-medium">
                  {entry.name}
                </Link>
              </td>
              <td className="whitespace-nowrap p-3">{entry.location ?? '–'}</td>
              <td className="whitespace-nowrap p-3">
                <RatingStat rating={entry.rating} reviewCount={entry.reviewCount} locale={locale} isCompact />
              </td>
              {isFilterable ? (
                <td className="whitespace-nowrap p-3">
                  {entry.budgetFrom != null
                    ? formatCurrency({ value: entry.budgetFrom, locale, rounded: true })
                    : t('longtail:ranking.table.onRequest')}
                </td>
              ) : null}
              <td className="p-3">{entry.publishedWorkCount}</td>
              <td className="p-3 font-semibold text-secondary-950">{entry.score}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollableTable>
  );
};
